import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid
} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const WebhookSchema = Yup.object().shape({
  webhookUrl: Yup.string()
    .url("URL inválida")
    .required("Digite a URL do webhook"),
  method: Yup.string()
    .oneOf(["GET", "POST", "PUT", "PATCH", "DELETE"], "Método inválido")
    .required("Selecione o método HTTP"),
  payload: Yup.string(),
  headers: Yup.string(),
  timeout: Yup.number()
    .min(1, "Mínimo 1 segundo")
    .max(60, "Máximo 60 segundos")
    .required("Digite o timeout"),
});

const FlowBuilderWebhookModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const initialState = {
    webhookUrl: "",
    method: "POST",
    payload: "",
    headers: "",
    timeout: 10,
  };

  const [activeModal, setActiveModal] = useState(false);
  const [labels, setLabels] = useState({
    title: "Adicionar Webhook",
    btn: "Adicionar",
  });

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar Webhook",
        btn: "Salvar",
      });
      setActiveModal(true);
    } else if (open === "create") {
      setLabels({
        title: "Adicionar Webhook",
        btn: "Adicionar",
      });
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSave = (values) => {
    if (open === "edit") {
      handleClose();
      onUpdate({
        ...data,
        data: values,
      });
    } else if (open === "create") {
      handleClose();
      onSave(values);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth="md"
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">{labels.title}</DialogTitle>
        <Formik
          initialValues={data?.data || initialState}
          enableReinitialize={true}
          validationSchema={WebhookSchema}
          onSubmit={handleSave}
        >
          {({ touched, errors, isSubmitting, values }) => (
            <Form>
              <DialogContent dividers>
                <Grid style={{ width: "100%", marginTop: 40 }} container spacing={2}>
                  <Grid item xs={12}>
                    <Typography>URL do Webhook</Typography>
                    <Field
                      as={TextField}
                      name="webhookUrl"
                      error={touched.webhookUrl && Boolean(errors.webhookUrl)}
                      helperText={touched.webhookUrl && errors.webhookUrl}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      placeholder="https://api.exemplo.com/webhook"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography>Método HTTP</Typography>
                    <FormControl fullWidth margin="dense" variant="outlined">
                      <Field
                        as={Select}
                        name="method"
                        style={{ width: "95%" }}
                      >
                        <MenuItem value="GET">GET</MenuItem>
                        <MenuItem value="POST">POST</MenuItem>
                        <MenuItem value="PUT">PUT</MenuItem>
                        <MenuItem value="PATCH">PATCH</MenuItem>
                        <MenuItem value="DELETE">DELETE</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography>Headers (JSON)</Typography>
                    <Field
                      as={TextField}
                      name="headers"
                      error={touched.headers && Boolean(errors.headers)}
                      helperText={touched.headers && errors.headers || 'Ex: {"Authorization": "Bearer token"}'}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      multiline
                      rows={3}
                      placeholder='{"Content-Type": "application/json"}'
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography>Payload (JSON)</Typography>
                    <Field
                      as={TextField}
                      name="payload"
                      error={touched.payload && Boolean(errors.payload)}
                      helperText={touched.payload && errors.payload || 'Variáveis: ${ticketId}, ${ticketStatus}, ${contactId}, ${contactName}, ${contactNumber}, ${contactEmail}, ${userId}, ${userName}, ${flowId}, ${timestamp}'}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      multiline
                      rows={3}
                      placeholder='{"message": "Olá ${contactName}"}'
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography>Timeout (segundos)</Typography>
                    <Field
                      as={TextField}
                      name="timeout"
                      type="number"
                      error={touched.timeout && Boolean(errors.timeout)}
                      helperText={touched.timeout && errors.timeout}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      placeholder="10"
                    />
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  variant="outlined"
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  className={classes.btnWrapper}
                  disabled={isSubmitting}
                >
                  {labels.btn}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default FlowBuilderWebhookModal;
